<template>
  <div
    v-if="isOpen"
    class="price-modal-shell fixed inset-0 overflow-y-auto"
    style="z-index: 70"
    aria-labelledby="price-update-title"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="price-modal-backdrop fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
      @click="handleClose"
    ></div>

    <div
      class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0"
    >
      <div
        class="price-modal-panel relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
      >
        <div class="price-modal-header flex items-center justify-between mb-3">
          <div class="price-modal-title-wrap">
            <span class="price-modal-icon">
              <vue-feather type="tag" size="18" />
            </span>
            <div>
              <p class="price-modal-eyebrow">{{ priceTypeLabel }} pricing</p>
              <h4
                id="price-update-title"
                class="text-sm font-semibold text-gray-900 dark:text-white"
              >
                Update sale price
              </h4>
            </div>
          </div>
          <button
            @click="handleClose"
            class="price-modal-close text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            type="button"
          >
            <vue-feather type="x" size="18" />
          </button>
        </div>

        <div
          v-if="!isProductPrice"
          class="text-xs text-gray-500 dark:text-gray-400 mb-3"
        >
          Current: {{ formatPrice(currentPrice) }}
        </div>

        <div v-if="isProductPrice" class="space-y-3">
          <div
            v-if="defaultPricingUnit"
            class="rounded-md border border-gray-200 dark:border-gray-700 p-3"
          >
            <label
              class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Price per {{ defaultPricingUnit.label }}
            </label>
            <div class="flex items-center gap-2">
              <input
                v-model.number="productPrices.default"
                type="number"
                :min="minPriceValue"
                step="0.01"
                :placeholder="`Price per ${defaultPricingUnit.label}`"
                class="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span
                class="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400"
              >
                {{ currency }}/{{ defaultPricingUnit.label }}
              </span>
            </div>
          </div>

          <button
            @click="updatePrice"
            :disabled="updating || !isValidPrice"
            class="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            <span
              v-if="updating"
              class="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"
            ></span>
            <span v-else>Update Prices</span>
          </button>
        </div>

        <div v-else class="flex items-center gap-2">
          <input
            v-model.number="pendingPrice"
            type="number"
            :min="minPriceValue"
            step="0.01"
            placeholder="New price"
            class="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            @click="updatePrice"
            :disabled="updating || !isValidPrice"
            class="price-save-btn"
            type="button"
          >
            <span
              v-if="updating"
              class="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"
            ></span>
            <span v-else>Update Price</span>
          </button>
        </div>

        <p class="price-modal-help text-xs text-gray-500 dark:text-gray-400 mt-2">
          Enter a new sale price. The catalog table will update after saving.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import http from "@/helper/http";
import { toast } from "vue3-toastify";

export default {
  name: "PriceUpdateModal",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    priceType: {
      type: String,
      required: true,
      validator: (value) => ["product", "service"].includes(value),
    },
    itemId: {
      type: Number,
      default: null,
    },
    itemName: {
      type: String,
      default: "",
    },
    currentPrice: {
      type: Number,
      default: 0,
    },
    currency: {
      type: String,
      default: "RWF",
    },
    minPrice: {
      type: Number,
      default: 0,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  emits: ["price-updated", "close"],
  setup(props, { emit }) {
    const pendingPrice = ref(null);
    const productPrices = ref({ default: null, base: null });
    const updating = ref(false);
    const minPriceValue = computed(() => props.minPrice);
    const isProductPrice = computed(() => props.priceType === "product");

    const normalizeUnitCode = (unitCode) => {
      if (unitCode === null || unitCode === undefined) return "";
      return String(unitCode).trim().toUpperCase();
    };

    const formatUnitLabel = (unitCode) => {
      const normalized = normalizeUnitCode(unitCode);
      if (!normalized) return "unit";
      return normalized
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
        .join(" ");
    };

    const productItem = computed(() => props.item || {});

    const salePrices = computed(() => {
      const item = productItem.value;
      return (
        item.salePrices ||
        item.sale_prices ||
        item.originalData?.sale_prices ||
        item.product?.sale_prices ||
        []
      );
    });

    const findSalePriceForUnit = (unitCode) => {
      const normalized = normalizeUnitCode(unitCode);
      if (!normalized) return null;

      const salePrice = salePrices.value.find(
        (price) => normalizeUnitCode(price?.price_unit_code) === normalized
      );
      if (salePrice) return salePrice;

      const priceRaw = productItem.value.priceRaw || productItem.value.price;
      if (normalizeUnitCode(priceRaw?.price_unit_code) === normalized) {
        return priceRaw;
      }

      return null;
    };

    const defaultPricingUnit = computed(() => {
      const item = productItem.value;
      const unitCode =
        item.default_quantity_unit ||
        item.product?.default_quantity_unit ||
        item.originalData?.product?.default_quantity_unit ||
        item.priceRaw?.price_unit_code ||
        item.price?.price_unit_code ||
        item.quantity_unit;

      const normalized = normalizeUnitCode(unitCode);
      if (!normalized) return null;

      return {
        code: normalized,
        label: formatUnitLabel(normalized),
      };
    });

    const isFilledPrice = (price) => {
      return price !== null && price !== undefined && price !== "";
    };

    const isUsablePrice = (price) => {
      return isFilledPrice(price) && Number(price) >= minPriceValue.value;
    };

    const isValidProductPrices = computed(() => {
      return isUsablePrice(productPrices.value.default);
    });

    const isValidPrice = computed(() => {
      if (isProductPrice.value) return isValidProductPrices.value;
      return (
        typeof pendingPrice.value === "number" &&
        pendingPrice.value >= minPriceValue.value
      );
    });

    const priceTypeLabel = computed(() => {
      return props.priceType === "service" ? "Service" : "Product";
    });

    const formatPrice = (price) => {
      if (price === null || price === undefined) {
        return `${props.currency} 0.00`;
      }
      return `${props.currency} ${parseFloat(price).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };

    const handleClose = () => {
      emit("close");
    };

    const setProductPriceInputs = () => {
      const defaultSalePrice = findSalePriceForUnit(
        defaultPricingUnit.value?.code
      );

      productPrices.value = {
        default:
          defaultSalePrice?.unit_price ??
          (defaultPricingUnit.value ? props.currentPrice ?? null : null),
        base: null,
      };
    };

    const buildProductPricePayloads = () => {
      const payloads = [];
      if (defaultPricingUnit.value && isFilledPrice(productPrices.value.default)) {
        payloads.push({
          product_id: props.itemId,
          unit_price: Number(productPrices.value.default),
          price_unit_code: defaultPricingUnit.value.code,
          default: true,
        });
      }

      return payloads;
    };

    const updatePrice = async () => {
      if (!isValidPrice.value || !props.itemId) return;

      updating.value = true;
      try {
        const endpoint =
          props.priceType === "service"
            ? "/v1/store/service/sale/price"
            : "/v1/store/product/sale/price";

        let unitPrice = pendingPrice.value;
        let updatedPrices = [];

        if (isProductPrice.value) {
          const responses = [];
          for (const payload of buildProductPricePayloads()) {
            const response = await http.post(endpoint, payload);
            responses.push(response.data);
          }
          updatedPrices = responses;
          unitPrice =
            responses.find((price) => price?.is_default)?.unit_price ??
            responses[0]?.unit_price ??
            unitPrice;
        } else {
          await http.post(endpoint, {
            service_id: props.itemId,
            unit_price: unitPrice,
          });
        }

        toast.success(`${priceTypeLabel.value} price updated successfully!`);
        emit("price-updated", {
          type: props.priceType,
          itemId: props.itemId,
          newPrice: unitPrice,
          currency: props.currency,
          updatedPrices,
        });
        handleClose();
      } catch (err) {
        console.error(`Failed to update ${props.priceType} price:`, err);
        const apiMessage = err?.response?.data?.error?.message;
        if (apiMessage) {
          toast.error(apiMessage);
        } else if (err?.response) {
          const status = err?.response?.status;
          if (status === 400 || status === 422) {
            toast.error(
              `The price you entered is not valid. Please enter a value of at least ${props.currency} ${minPriceValue.value}.`
            );
          } else {
            toast.error(
              "An error occurred while updating the price. Please try again."
            );
          }
        } else {
          toast.error(
            "Failed to update price. Check your network connection and try again."
          );
        }
      } finally {
        updating.value = false;
      }
    };

    watch(
      () => props.isOpen,
      (isOpen) => {
        if (isOpen) {
          pendingPrice.value = props.currentPrice ?? null;
          setProductPriceInputs();
        } else {
          pendingPrice.value = null;
          productPrices.value = { default: null, base: null };
        }
      }
    );

    watch(
      () => props.currentPrice,
      (price) => {
        if (props.isOpen) {
          pendingPrice.value = price ?? null;
          setProductPriceInputs();
        }
      }
    );

    watch(
      () => props.item,
      () => {
        if (props.isOpen) {
          setProductPriceInputs();
        }
      },
      { deep: true }
    );

    return {
      pendingPrice,
      productPrices,
      minPriceValue,
      updating,
      isValidPrice,
      isProductPrice,
      priceTypeLabel,
      defaultPricingUnit,
      formatPrice,
      updatePrice,
      handleClose,
    };
  },
};
</script>

<style scoped>
.price-modal-backdrop {
  background: rgba(15, 23, 42, 0.54);
  backdrop-filter: blur(8px);
}

.price-modal-panel {
  background:
    radial-gradient(circle at 92% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 14%, transparent), transparent 34%),
    var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 26px;
  box-shadow: 0 32px 90px rgba(15, 23, 42, 0.32);
}

.price-modal-title-wrap {
  align-items: center;
  display: flex;
  gap: 12px;
}

.price-modal-icon {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 13%, transparent);
  border-radius: 16px;
  color: var(--hs-primary, #3293ec);
  display: inline-flex;
  height: 46px;
  justify-content: center;
  width: 46px;
}

.price-modal-eyebrow {
  color: var(--hs-muted, #64748b);
  font-size: 0.7rem;
  font-weight: 950;
  letter-spacing: 0.12em;
  margin: 0 0 4px;
  text-transform: uppercase;
}

.price-modal-header h4 {
  color: var(--hs-text, #0f172a);
  font-size: 1.25rem;
  font-weight: 950;
  margin: 0;
}

.price-modal-close {
  align-items: center;
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  border-radius: 14px;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease;
  width: 38px;
}

.price-modal-close:hover {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  transform: translateY(-1px);
}

.price-current-card {
  align-items: stretch;
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 72%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 18px;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-top: 18px;
  min-height: 58px;
  padding: 0 16px;
}

.price-current-card__item,
.price-current-card__price {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.price-current-card__price {
  text-align: right;
}

.price-current-card span {
  color: var(--hs-muted, #64748b);
  font-size: 0.78rem;
  font-weight: 900;
}

.price-current-card strong {
  color: var(--hs-text, #0f172a);
  font-size: 1.1rem;
  font-weight: 950;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.price-input-field {
  display: grid;
  gap: 8px;
  margin-top: 16px;
}

.price-input-field > span {
  color: var(--hs-muted, #64748b);
  font-size: 0.74rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.price-input-control {
  align-items: center;
  background: var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 18px;
  display: flex;
  min-height: 54px;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.price-input-control:focus-within {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 48%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
}

.price-input-currency {
  align-items: center;
  align-self: stretch;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
  color: var(--hs-primary, #3293ec);
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 950;
  padding: 0 14px;
}

.price-input-control input {
  background: transparent;
  border: 0;
  color: var(--hs-text, #0f172a);
  flex: 1;
  font-size: 1.05rem;
  font-weight: 900;
  min-width: 0;
  outline: none;
  padding: 0 14px;
}

.price-modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 18px;
}

.price-cancel-btn,
.price-save-btn {
  align-items: center;
  border-radius: 15px;
  display: inline-flex;
  font-size: 0.88rem;
  font-weight: 950;
  justify-content: center;
  min-height: 42px;
  padding: 0 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.price-cancel-btn {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  color: var(--hs-text, #0f172a);
}

.price-save-btn {
  background: linear-gradient(135deg, var(--hs-primary, #3293ec), #2563eb);
  box-shadow: 0 14px 30px color-mix(in srgb, var(--hs-primary, #3293ec) 26%, transparent);
  color: white;
}

.price-cancel-btn:hover,
.price-save-btn:not(:disabled):hover {
  transform: translateY(-1px);
}

.price-save-btn:disabled {
  cursor: not-allowed;
  opacity: 0.56;
}

.price-modal-help {
  color: var(--hs-muted, #64748b);
  line-height: 1.5;
}

@media (max-width: 520px) {
  .price-modal-actions {
    flex-direction: column-reverse;
  }

  .price-cancel-btn,
  .price-save-btn {
    width: 100%;
  }
}
</style>
